/**
 * News: the list of posts, and one post.
 *
 * Two things worth stating about the data:
 *
 *  1. `publishedAt` is NULLABLE on the wire even for a published post, because
 *    `News.published` and `News.publishedAt` are separate columns in core's
 *    schema and nothing enforces that setting one sets the other. So every
 *    date here is conditional; there is no `new Date(null)` producing an
 *    "Invalid Date" on the page.
 *  2. In a preview build `GET /cms/news` returns an empty page and
 *    `GET /cms/news/:slug` returns 404, deliberately. The list renders its
 *    empty state and the article renders the preview notice — neither claims
 *    the post was deleted.
 *  3. `tag` is operator data, edited on `/admin/news` and returned by the
 *    endpoint, and until now nothing on the public site rendered it: the field
 *    existed in the console, in the database and on the wire, and a category
 *    typed into it reached nobody. It is drawn here, on the card and at the top
 *    of the article, and it is DECORATION-FREE — no colour coding, no icon,
 *    because an operator can type any word and a palette keyed to "announcement"
 *    would silently drop "hotfix" into a default nobody chose.
 */
import { formatDate, t } from '@/engine/i18n';
import {
  fetchNews,
  fetchNewsArticle,
  type NewsArticle,
  type NewsPage,
} from './cms-api';
import {
  BUTTON,
  pressable,
  TAP_LINK,
  createPageFrame,
  el,
  mountAsyncPage,
  renderEmpty,
} from './page-parts';
import { applyProseClasses, renderRichText } from './rich-text';

export const NEWS_INDEX_HREF = '/news';
export const newsHref = (slug: string): string => `/news/${slug}`;

/** `?page=` for the list, clamped to something a URL cannot lie about. */
export function pageFromSearch(search: string): number {
  const raw = new URLSearchParams(search).get('page');
  const parsed = Number.parseInt(raw ?? '1', 10);
  return Number.isFinite(parsed) && parsed > 1 ? parsed : 1;
}

/* ═══════════════════════════════════════════════════════════════ the list ══ */

export function renderNewsList(root: HTMLElement, page = 1): () => void {
  const doc = root.ownerDocument;
  const { main, body } = createPageFrame(doc, {
    title: t('news.title'),
    subtitle: t('news.subtitle'),
  });
  root.replaceChildren(main);

  return mountAsyncPage<NewsPage>(body, {
    load: (signal) => fetchNews(page, { signal }),
    notFound: { title: t('news.not_found_title'), body: t('news.not_found_body') },
    render: (data, host) => {
      if (data.items.length === 0) {
        renderEmpty(host, t('news.empty'));
        return;
      }

      const list = el(doc, 'ul', 'space-y-4');
      for (const article of data.items) list.appendChild(renderCard(doc, article));
      host.appendChild(list);

      const pages = Math.max(1, Math.ceil(data.total / Math.max(1, data.pageSize)));
      if (pages > 1) host.appendChild(renderPager(doc, data.page, pages));
    },
  });
}

function renderCard(doc: Document, article: NewsArticle): HTMLElement {
  const item = el(doc, 'li');
  // Each post is a long box, stacked one after another (owner ask): a bordered
  // surface card that lifts its border on hover, spanning the content width.
  const card = el(
    doc,
    'article',
    'rounded-lg border border-border bg-surface p-5 sm:p-6 ' +
      'transition-colors duration-150 hover:border-gold/50',
  );

  // The banner bleeds to the card's edges (negative margins undo the padding)
  // and links to the post like the headline does. Hidden from screen readers:
  // the headline link right below it already says where it goes.
  if (article.imageUrl !== null && article.imageUrl !== '') {
    const banner = el(
      doc,
      'a',
      '-mx-5 -mt-5 sm:-mx-6 sm:-mt-6 mb-4 block overflow-hidden rounded-t-lg border-b border-border',
    );
    banner.href = newsHref(article.slug);
    banner.tabIndex = -1;
    banner.setAttribute('aria-hidden', 'true');
    banner.setAttribute('data-testid', 'news-card-image');
    const img = el(doc, 'img', 'block aspect-video w-full object-cover');
    img.src = article.imageUrl;
    img.alt = '';
    img.loading = 'lazy';
    img.decoding = 'async';
    img.width = 1280;
    img.height = 720;
    banner.appendChild(img);
    card.appendChild(banner);
  }

  const tag = renderTag(doc, article.tag);
  if (tag !== null) card.appendChild(tag);

  const heading = el(doc, 'h2', 'font-heading text-xl text-text');
  const link = el(doc, 'a', 'hover:underline text-gold', article.title);
  link.href = newsHref(article.slug);
  heading.appendChild(link);
  card.appendChild(heading);

  if (article.publishedAt !== null) {
    const time = el(doc, 'time', 'block mt-1 text-sm text-text-dim');
    time.dateTime = article.publishedAt;
    time.textContent = t('news.published_on', {
      date: formatDate(new Date(article.publishedAt)),
    });
    card.appendChild(time);
  }

  card.appendChild(el(doc, 'p', 'mt-2 text-text-dim leading-relaxed', article.summary));

  const more = el(doc, 'a', `${TAP_LINK} gap-1.5`, t('news.read_more'));
  more.href = newsHref(article.slug);
  pressable(more, 'action-next', undefined, true);
  // The card title is already a link to the same place, so this one is
  // redundant to a screen reader unless it says which post it opens.
  more.setAttribute('aria-label', `${t('news.read_more')}: ${article.title}`);
  card.appendChild(more);

  item.appendChild(card);
  return item;
}

function renderPager(doc: Document, current: number, pages: number): HTMLElement {
  const nav = el(doc, 'nav', 'mt-10 pt-6 border-t border-border flex gap-3 items-center');
  nav.setAttribute('aria-label', t('news.title'));

  const link = (target: number, label: string, forward: boolean): HTMLAnchorElement => {
    const a = el(doc, 'a', BUTTON, label);
    a.href = target <= 1 ? NEWS_INDEX_HREF : `${NEWS_INDEX_HREF}?page=${target}`;
    // Arrows that say which way the pager goes, not just two words.
    return pressable(a, forward ? 'action-next' : 'action-prev', undefined, forward);
  };

  if (current > 1) nav.appendChild(link(current - 1, t('common.back'), false));
  if (current < pages) nav.appendChild(link(current + 1, t('common.next'), true));
  return nav;
}

/* ════════════════════════════════════════════════════════════ one article ══ */

export function renderNewsArticle(root: HTMLElement, slug: string): () => void {
  const doc = root.ownerDocument;
  const { main, body } = createPageFrame(doc, {
    title: t('news.title'),
    backHref: NEWS_INDEX_HREF,
    backLabel: t('news.back_to_news'),
  });
  root.replaceChildren(main);
  const h1 = main.querySelector('h1') as HTMLHeadingElement;

  return mountAsyncPage<NewsArticle>(body, {
    load: (signal) => fetchNewsArticle(slug, { signal }),
    notFound: { title: t('news.not_found_title'), body: t('news.not_found_body') },
    render: (article, host) => {
      h1.textContent = article.title;

      const tag = renderTag(doc, article.tag);
      if (tag !== null) host.appendChild(tag);

      if (article.publishedAt !== null) {
        const time = el(doc, 'time', 'block text-sm text-text-dim');
        time.dateTime = article.publishedAt;
        time.textContent = t('news.published_on', {
          date: formatDate(new Date(article.publishedAt)),
        });
        host.appendChild(time);
      }

      if (article.imageUrl !== null && article.imageUrl !== '') {
        const img = el(doc, 'img', 'mt-4 w-full h-auto rounded border border-border');
        img.src = article.imageUrl;
        // Decorative until the CMS has an alt-text field: an invented
        // description is worse than none, and repeating the headline makes a
        // screen reader say it twice.
        img.alt = '';
        img.loading = 'lazy';
        host.appendChild(img);
      }

      const content = el(doc, 'article', 'mt-6');
      renderRichText(content, article.content, {
        externalLinkLabel: t('a11y.external_link'),
        doc,
      });
      applyProseClasses(content);
      host.appendChild(content);

      const back = el(doc, 'a', `${TAP_LINK} mt-10 gap-1.5`, t('news.back_to_news'));
      back.href = NEWS_INDEX_HREF;
      pressable(back, 'header-back');
      host.appendChild(back);
    },
  });
}

/* ═════════════════════════════════════════════════════════════════ the tag ══ */

/**
 * The post's category, as the operator typed it.
 *
 * Blank is the common case — `tag` has no default and nothing has ever forced
 * one — so an untagged post gets NOTHING, not an empty pill. The label is in
 * the accessibility tree only: sighted readers see a chip above a headline and
 * know what it is, while a screen reader would otherwise announce a bare word
 * ("hotfix") with no clue what role it plays.
 */
function renderTag(doc: Document, tag: string): HTMLElement | null {
  const value = tag.trim();
  if (value === '') return null;
  const chip = el(
    doc,
    'p',
    'mb-1 inline-flex items-center rounded border border-border px-2 py-0.5 ' +
      'text-xs uppercase tracking-wide text-text-dim',
  );
  chip.setAttribute('data-testid', 'news-tag');
  const label = el(doc, 'span', 'sr-only', `${t('news.tag_label')}: `);
  chip.append(label, doc.createTextNode(value));
  return chip;
}
